import React, { useEffect, useState } from 'react';
import api, { getErrorMessage } from '../../services/api';
import { hasJudicialPricing } from '@claimup/shared/helpers/judicial-pricing';
import { JUDICIAL_INPUTS } from '../../components/recovery-pricing/RecoveryPricingBlock';

/**
 * La pestaña «Precios» de Facturación (docs/precios-en-facturacion-plan.md): la
 * tarifa de la empresa, AR y recobro, en solo lectura. Los precios los pone el
 * admin desde el perfil de la empresa; aquí solo se ven.
 */
const eur = n =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
const pct = n => `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2 }).format(n || 0)} %`;
const money = n => (n === null || n === undefined ? '—' : eur(n));

function ArTariff({ ar }) {
  return (
    <section className='bg-white border border-slate-200 rounded-lg'>
      <header className='px-5 pt-4'>
        <h2 className='font-semibold text-slate-800'>Gestión propia</h2>
        <p className='text-sm text-slate-500 mt-1'>
          Un precio fijo al mes, sin IVA, según los euros que tenéis en gestión en el mes: lo activo el día 1 más lo que
          entra durante el mes.
        </p>
      </header>
      {ar.tiers.length === 0 ? (
        <p className='px-5 py-4 text-sm text-slate-500'>Todavía no está cargada. Pregúntanos.</p>
      ) : (
        <table className='min-w-full text-sm mt-3'>
          <thead className='bg-slate-50 text-slate-500 text-xs uppercase'>
            <tr>
              <th className='text-left px-5 py-2 font-semibold'>Euros gestionados en el mes</th>
              <th className='text-right px-5 py-2 font-semibold'>Precio al mes (sin IVA)</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {ar.tiers.map(t => (
              <tr key={`${t.from}-${t.to}`}>
                <td className='px-5 py-2 text-slate-700'>
                  {t.to === null || t.to === undefined ? `Desde ${eur(t.from)}` : `De ${eur(t.from)} a ${eur(t.to)}`}
                </td>
                <td className='px-5 py-2 text-right tabular-nums font-medium text-slate-800'>{eur(t.price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function RecoveryTariff({ recovery }) {
  const judicial = hasJudicialPricing(recovery.judicial);
  return (
    <section className='bg-white border border-slate-200 rounded-lg px-5 py-4 space-y-4'>
      <div>
        <h2 className='font-semibold text-slate-800'>Recobro</h2>
        <p className='text-sm text-slate-500 mt-1'>Honorarios sobre lo que se cobra, sin IVA.</p>
      </div>
      <dl className='grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm'>
        <div>
          <dt className='text-slate-500'>Extrajudicial (recobro externo)</dt>
          <dd className='text-lg font-semibold text-slate-800'>{pct(recovery.extrajudicial_pct)} de lo cobrado</dd>
        </div>
        <div>
          <dt className='text-slate-500'>Judicial (success fee)</dt>
          <dd className='text-lg font-semibold text-slate-800'>
            {recovery.bill_judicial === false
              ? 'No se factura'
              : recovery.judicial_pct === null || recovery.judicial_pct === undefined
              ? '—'
              : `${pct(recovery.judicial_pct)} de lo cobrado`}
          </dd>
        </div>
        <div>
          <dt className='text-slate-500'>Tope de honorarios al mes</dt>
          <dd className='text-lg font-semibold text-slate-800'>
            {recovery.monthly_cap === null || recovery.monthly_cap === undefined ? 'Sin tope' : eur(recovery.monthly_cap)}
          </dd>
        </div>
      </dl>

      {judicial && (
        <div className='pt-3 border-t border-slate-100'>
          <h3 className='font-medium text-slate-800'>Judicial: por demanda presentada</h3>
          <p className='text-xs text-slate-500 mb-2'>
            Se factura el mes en que se presenta la demanda, según el tipo (monitorio o verbal) y la deuda reclamada. La
            tasa judicial va aparte y sin IVA; si el juzgado cobra otra, se factura la real.
          </p>
          <dl className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm'>
            {JUDICIAL_INPUTS.map(j => (
              <div key={j.field} className='flex justify-between gap-3 border-b border-slate-50 py-1'>
                <dt className='text-slate-600'>{j.label}</dt>
                <dd className='tabular-nums text-slate-800'>{money(recovery.judicial?.[j.field])}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </section>
  );
}

function TariffTab({ companyId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/recovery-billing/companies/${companyId}/tariff`)
      .then(({ data: res }) => setData(res.data))
      .catch(err => setError(getErrorMessage(err)));
  }, [companyId]);

  if (error) return <p className='text-sm text-red-700'>{error}</p>;
  if (!data) return <p className='text-sm text-slate-500'>Cargando…</p>;
  if (!data.ar && !data.recovery) {
    return (
      <p className='bg-white border border-slate-200 rounded-lg px-5 py-6 text-sm text-slate-500'>
        Todavía no tenemos vuestra tarifa cargada. Pregúntanos.
      </p>
    );
  }
  return (
    <div className='space-y-4'>
      {data.ar && <ArTariff ar={data.ar} />}
      {data.recovery && <RecoveryTariff recovery={data.recovery} />}
    </div>
  );
}

export default TariffTab;
